import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { ChevronDownIcon, XIcon } from '@heroicons/react/outline';
import { PencilIcon } from '@heroicons/react/solid';

const MAX_BASE64_LENGTH = 190_000;

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (signatureDataUrl: string) => void;
  isSubmitting: boolean;
  title?: string;
}

const SignatureModal: FC<SignatureModalProps> = ({
  open,
  onClose,
  onConfirm,
  isSubmitting,
  title = '답례품 신청을 위한 서명',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [showLegalInfo, setShowLegalInfo] = useState(false);
  const legalInfoRef = useRef<HTMLDivElement>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  const getCanvasPoint = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    },
    [],
  );

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }, []);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(initCanvas);
    }
  }, [open, initCanvas]);

  const startDraw = useCallback(
    (clientX: number, clientY: number) => {
      isDrawingRef.current = true;
      lastPointRef.current = getCanvasPoint(clientX, clientY);
    },
    [getCanvasPoint],
  );

  const draw = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDrawingRef.current || !lastPointRef.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;

      const point = getCanvasPoint(clientX, clientY);
      ctx.beginPath();
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
      ctx.lineTo(point.x, point.y);
      ctx.strokeStyle = '#1c1917';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      lastPointRef.current = point;
      if (!hasDrawn) setHasDrawn(true);
    },
    [getCanvasPoint, hasDrawn],
  );

  const endDraw = useCallback(() => {
    isDrawingRef.current = false;
    lastPointRef.current = null;
  }, []);

  const handleClear = useCallback(() => {
    initCanvas();
  }, [initCanvas]);

  const handleConfirm = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let dataUrl = canvas.toDataURL('image/png');
    if (dataUrl.length <= MAX_BASE64_LENGTH) {
      onConfirm(dataUrl);
      return;
    }

    let quality = 0.8;
    while (dataUrl.length > MAX_BASE64_LENGTH && quality > 0.1) {
      dataUrl = canvas.toDataURL('image/jpeg', quality);
      quality -= 0.1;
    }
    if (dataUrl.length <= MAX_BASE64_LENGTH) {
      onConfirm(dataUrl);
    }
  }, [onConfirm]);

  useEffect(() => {
    if (!open) return;
    const handleBodyScroll = (e: TouchEvent) => {
      if (canvasRef.current?.contains(e.target as Node)) {
        e.preventDefault();
      }
    };
    document.addEventListener('touchmove', handleBodyScroll, { passive: false });
    return () => document.removeEventListener('touchmove', handleBodyScroll);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
      <div className="w-full max-w-lg animate-slideUp rounded-t-2xl bg-white pb-safe">
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
          <h3 className="text-lg font-bold text-stone-800">서명 확인</h3>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-full p-1 text-stone-400 transition hover:bg-stone-100"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* 안내 문구 */}
        <div className="px-5 pt-4">
          <div className="rounded-lg bg-stone-50 px-4 py-3">
            <div className="flex items-start gap-2.5">
              <PencilIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-stone-500" />
              <div>
                <p className="text-sm font-semibold text-stone-700">
                  {title}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-stone-400">
                  서명은 답례품(모바일 교환권)을 지급하는 용도로만 수집되며, 그 외
                  목적으로는 일체 사용되지 않습니다. 수집된 서명은 관련 법령에 따라 보관
                  후 파기됩니다.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowLegalInfo((v) => {
                      if (!v) {
                        requestAnimationFrame(() => {
                          legalInfoRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                          });
                        });
                      }
                      return !v;
                    });
                  }}
                  className="mt-1.5 ml-auto flex items-center gap-0.5 text-[11px] text-stone-400 underline-offset-2 hover:underline"
                >
                  자세히 보기
                  <ChevronDownIcon
                    className={`h-3 w-3 transition-transform ${
                      showLegalInfo ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {showLegalInfo && (
                  <div
                    ref={legalInfoRef}
                    className="mt-2 max-h-32 space-y-2.5 overflow-y-auto border-t border-stone-200 pt-2 text-[11px] leading-relaxed text-stone-400"
                  >
                    <div>
                      <p className="font-semibold text-stone-500">수집 목적</p>
                      <p>기프티콘 발송 및 정상 수령 확인(증빙), 중복·부정 수납 방지</p>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-500">수집 항목</p>
                      <p>휴대폰 번호, 수령 확인 서명</p>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-500">보관 근거</p>
                      <p>전자상거래법 제6조: 거래기록 및 재화의공급에 관한 기록보존</p>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-500">보유 기간</p>
                      <p>관련 법령에 따른 보존 기간(5년) 경과 후 즉시 파기</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 서명 캔버스 */}
        <div className="px-5 pt-4">
          <div className="relative overflow-hidden rounded-xl border-2 border-dashed border-stone-300 bg-white">
            <canvas
              ref={canvasRef}
              width={600}
              height={240}
              className="w-full touch-none"
              style={{ height: 160 }}
              onMouseDown={(e) => startDraw(e.clientX, e.clientY)}
              onMouseMove={(e) => draw(e.clientX, e.clientY)}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={(e) => {
                const t = e.touches[0];
                if (t) startDraw(t.clientX, t.clientY);
              }}
              onTouchMove={(e) => {
                const t = e.touches[0];
                if (t) draw(t.clientX, t.clientY);
              }}
              onTouchEnd={endDraw}
            />
            {!hasDrawn && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="text-sm text-stone-300">여기에 서명해 주세요</span>
              </div>
            )}
          </div>

          <div className="mt-2 flex justify-end">
            <button
              onClick={handleClear}
              disabled={isSubmitting}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-stone-500 transition hover:bg-stone-100"
            >
              다시 쓰기
            </button>
          </div>
        </div>

        {/* 버튼 */}
        <div className="px-5 pb-5 pt-3">
          <button
            onClick={handleConfirm}
            disabled={!hasDrawn || isSubmitting}
            className={`w-full rounded-lg py-4 text-base font-bold transition ${
              hasDrawn && !isSubmitting
                ? 'bg-stone-800 text-white active:bg-stone-900'
                : 'bg-stone-200 text-stone-400'
            }`}
          >
            {isSubmitting ? '처리 중...' : '받기'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
